import React from 'react';
import dayjs from 'dayjs';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { withSpecificationLine } from './LineSpecifications';
import type { GRNHeaderDTO } from '@/services/goodsReceiptNote.service';
import { getGrnLineHeatLines } from '@/services/goodsReceiptNote.service';
import { headerProjectLabel, projectLabel, hasMultipleLineProjects } from '@/utils/projectDisplay';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export interface PrintableGRNProps {
  grn: GRNHeaderDTO;
  companyName?: string;
  onClose?: () => void;
}

function amt(v: unknown): string {
  const n = Number(v ?? 0);
  return (Number.isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** GRN print — shared pagination engine with restored commercial fields. */
export const PrintableGRN: React.FC<PrintableGRNProps> = ({ grn, onClose }) => {
  const items = grn.items ?? [];
  const title = grn.receipt_type === 'Service' ? 'SERVICE RECEIPT VOUCHER' : 'GOODS RECEIPT VOUCHER';
  const supplierName = grn.supplier?.supplier_name ?? String(grn.supplier_id ?? '');
  const poNumber = grn.purchaseOrder?.po_number ?? String(grn.po_id ?? '');
  const milestoneText = grn.milestone
    ? `#${grn.milestone.milestone_number} ${grn.milestone.milestone_name}`
    : grn.po_milestone_id
      ? String(grn.po_milestone_id)
      : '';
  const projectText = headerProjectLabel(grn, items);
  const showLineProjectCol = hasMultipleLineProjects(items);
  const dateText = grn.grn_date ? dayjs(grn.grn_date).format('DD-MM-YYYY') : '';
  const billDateText = grn.grn_date ? dayjs(grn.grn_date).format('DD MMM YYYY') : '';
  const supplierInvoiceDateText =
    grn.supplier_delivery_note && dayjs(grn.supplier_delivery_note).isValid()
      ? dayjs(grn.supplier_delivery_note).format('DD MMM YYYY')
      : '';
  const apAccountText = grn.grnCreditAccount
    ? `${grn.grnCreditAccount.account_code ?? '-'} - ${grn.grnCreditAccount.account_name ?? '-'}`
    : '';
  const narrationParts = [
    supplierName ? `Supplier: ${supplierName}` : '',
    poNumber || '',
    projectText && projectText !== '-' ? projectText : '',
    grn.received_by_display ?? grn.received_by ?? '',
  ].filter(Boolean);

  const columns = [
    { key: 'sno', label: 'No' },
    { key: 'code', label: 'Code No' },
    { key: 'description', label: 'Description' },
    ...(showLineProjectCol ? [{ key: 'project', label: 'Project' }] : []),
    { key: 'qty', label: 'Qty', align: 'right' as const },
    { key: 'amount', label: 'Net Amount', align: 'right' as const },
  ];

  return (
    <EngineCommercialA4Print
      docType="grn"
      title={title}
      documentNumber={grn.grn_number || 'GRN'}
      partyName={supplierName}
      onClose={onClose}
      metaLines={[
        { label: 'Bill No', value: grn.grn_number || '' },
        { label: 'Bill Date', value: billDateText || dateText },
        { label: 'Supplier Invoice #', value: grn.supplier_invoice_number || '' },
        { label: 'Supplier Invoice Date', value: supplierInvoiceDateText },
        { label: 'Due Date', value: billDateText },
        { label: 'PO', value: poNumber },
        { label: 'Milestone', value: milestoneText },
        { label: 'GRN', value: grn.grn_number || '' },
        { label: 'Receiving Inspection', value: grn.qc_inspection_number || '' },
        { label: 'Project', value: projectText !== '-' ? projectText : '' },
        { label: 'Supplier', value: supplierName },
        { label: 'Accounts Payable', value: apAccountText },
      ].filter((m) => m.value)}
      columns={columns}
      lines={items.map((line, index) => {
        const qty = Number(line.accepted_quantity ?? line.received_quantity ?? 0);
        const unit = Number(line.purchaseOrderItem?.unit_price ?? 0);
        const heatLines = getGrnLineHeatLines(line);
        return {
          lineNumber: line.line_number ?? index + 1,
          description: withSpecificationLine(
            formatItemDescriptionWithProfile(line.supplier_description || line.item_description, line) || '—',
            line.specifications
          ),
          cells: {
            code: line.item_code || '—',
            ...(showLineProjectCol ? { project: projectLabel(line) } : {}),
            qty: qty.toLocaleString(),
            amount: amt(unit * qty),
          },
          extraLines: heatLines.length
            ? heatLines.map(
                (h) =>
                  `Heat ${h.heat_number || '—'} | ${h.dimension?.trim() || '—'} | Qty ${Number(h.quantity).toLocaleString()} | MTC ${h.mtc_document_number?.trim() || (h.mtc_document_id ? 'Linked' : '—')}`
              )
            : undefined,
        };
      })}
      finals={[
        { id: 'footer', heightPx: 56 },
        { id: 'ack', heightPx: 72 },
      ]}
      finalBlocks={{
        footer: (
          <div>
            <p>
              <strong>Narration:</strong> {narrationParts.join(' ') || '—'}
            </p>
            <p>
              <strong>Payment Terms:</strong> {grn.notes || 'As per PO / supplier terms'}
            </p>
          </div>
        ),
        ack: (
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
              <p>Inspected and found according to specification.</p>
              <p>
                <strong>Inspector:</strong> _______________________
              </p>
            </div>
            <div style={{ flex: 1 }}>
              <p>Received in good condition.</p>
              <p>
                <strong>Store Keeper:</strong> _______________________
              </p>
            </div>
          </div>
        ),
      }}
    />
  );
};
